import { useState } from 'react';
import { Button, Grid, LinearProgress } from '@mui/material';
import { Add, Delete, Edit, AccountCircleOutlined } from '@mui/icons-material/';
import { GridActionsCellItem, DataGrid, GridRowParams, GridRowId } from '@mui/x-data-grid';
import { blue, green, red } from '@mui/material/colors';
import Popup from '../../../components/Popup';
import { EmployeeDto } from '../../../api';
import ConfirmDialog, { IConfirmDialog } from '../../../components/form/controls/ConfirmDialog';
import { LightToolTip } from '../../../components/form/react-hook-form-components/ToolTipTheme';
import EmployeeForm from './EmployeeForm';
import useEmployees from '../../../hooks/useEmployees';
import useDataGridStyles from '../../../styles/useDataGridStyles';
import EmployeeQueries from './EmployeeQueries';
import useRoles from '../../../hooks/useRoles';
import ConfirmProfileDialog, { IConfirmProfileDialog } from '../../../components/form/controls/ConfirmProfileDialog';

export const EmployeesTable = () => {
  const [IsCreateFormOpen, SetIsCreateFormOpen] = useState(false);
  const [IsUpdateFormOpen, SetIsUpdateFormOpen] = useState(false);
  const { data: employees, isLoading: isLoadingEmployees, isError, error } = useEmployees();
  const { data: roles } = useRoles();
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeDto | undefined>(undefined);
  const classes = useDataGridStyles();
  const [deleteDialog, setDeleteDialog] = useState({
    isOpen: false,
    title: '',
    content: '',
  } as IConfirmDialog);
  const [profileDialog, setProfileDialog] = useState({
    isOpen: false,
    title: '',
    content: '',
  } as IConfirmProfileDialog);
  const { mutateCreateEmployee, mutateUpdateEmployee, mutateDeleteEmployee } = EmployeeQueries();

  if (isLoadingEmployees) return <LinearProgress />;
  if (isError) return <div>Error Loading Employees: {error}</div>;

  /**
   * Click event handler for the edit employee button
   * @param id the id of the employee to edit
   */
  const handleEditEmployee = async (id: GridRowId) => {
    const employee = employees?.find(employee => employee.id === +id);
    if (employee !== undefined) {
      setSelectedEmployee(employee);
      SetIsUpdateFormOpen(true);
    }
  };

  /**
   * Click event handler for the delete employee button
   * @param id the id of the employee to delete
   */
  const handleDeleteEmployee = (id: GridRowId) => {
    setDeleteDialog({
      isOpen: true,
      title: 'Are you sure you want to delete this employee?',
      content: "You can't undo this operation",
      onConfirm: () => {
        mutateDeleteEmployee(id, {
          onSuccess: closeDeleteDialog,
        });
      },
    } as IConfirmDialog);
  };

  const handleProfile = async (id: GridRowId) => {
    const employee = employees?.find(employee => employee.id === +id);
    if (employee?.profileLink) {
      window.open(employee?.profileLink);
    } else {
      setProfileDialog({
        isOpen: true,
        title: 'Employee does not have a profile link.',
        content: 'Would you like to go to the profile folder?',
        onConfirm: closeProfileDialog,
      } as IConfirmProfileDialog);
    }
  };

  /**
   * Closes the delete dialog
   */
  const closeDeleteDialog = () => {
    setDeleteDialog({
      ...deleteDialog,
      isOpen: false,
    });
  };

  /**
   * Closes the profile dialog
   */
  const closeProfileDialog = () => {
    setProfileDialog({
      ...profileDialog,
      isOpen: false,
    });
    window.open(
      'https://datacomunity.sharepoint.com/:f:/r/Teams/SoftwareBuild/Shared%20Documents/Profiles?csf=1&web=1&e=W6f0Yf'
    );
  };

  /**
   * The Data Grid column headings
   */
  const columns = [
    {
      field: 'firstName',
      headerName: 'First name',
      editable: false,
      flex: 1,
    },
    {
      field: 'lastName',
      headerName: 'Last name',
      editable: false,
      flex: 1,
    },
    {
      field: 'email',
      headerName: 'Email',
      editable: false,
      flex: 1.75,
    },
    {
      field: 'managerTeamName',
      headerName: 'Team',
      editable: false,
      flex: 1,
    },
    {
      field: 'roleName',
      headerName: 'Role',
      editable: false,
      flex: 1.25,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.75,
      type: 'actions',
      sortable: false,
      getActions: (params: GridRowParams) => {
        return [
          <GridActionsCellItem
            icon={
              <LightToolTip title="Profile" placement="bottom">
                <AccountCircleOutlined sx={{ color: params.row.profileLink ? green[600] : 'black' }} />
              </LightToolTip>
            }
            label="Edit"
            onClick={() => {
              handleProfile(params.id);
            }}
          />,
          <GridActionsCellItem
            icon={
              <LightToolTip title="Edit" placement="bottom">
                <Edit sx={{ color: blue[500] }} />
              </LightToolTip>
            }
            label="Edit"
            onClick={() => {
              handleEditEmployee(params.id);
            }}
          />,
          <GridActionsCellItem
            icon={
              <LightToolTip title="Delete" placement="bottom">
                <Delete sx={{ color: red[900] }} />
              </LightToolTip>
            }
            label="Delete"
            onClick={() => handleDeleteEmployee(params.id)}
          />,
        ];
      },
    },
  ];

  /**
   * Gets the Add Employee Button
   * @returns the Add Employee Button component
   */
  const AddEmployeeButton = () => {
    return (
      <Button startIcon={<Add />} size="medium" color="primary" onClick={() => SetIsCreateFormOpen(true)}>
        Add Employee
      </Button>
    );
  };

  /**
   * @returns Create Employee Popup Component
   */
  const CreateEmployeeFormPopup = () => {
    return (
      <Popup openPopup={IsCreateFormOpen} setOpenPopup={SetIsCreateFormOpen} title="Create Employee">
        <EmployeeForm
          employees={employees ?? []}
          onCancelHandler={() => SetIsCreateFormOpen(false)}
          onSubmitHandler={values => {
            mutateCreateEmployee(values, {
              onSuccess: () => SetIsCreateFormOpen(false),
            });
          }}
          roles={roles ?? []}
        />
      </Popup>
    );
  };

  /**
   * @returns Update Employee Form Popup Component
   */
  const UpdateEmployeeFormPopup = () => {
    return (
      <Popup openPopup={IsUpdateFormOpen} setOpenPopup={SetIsUpdateFormOpen} title="Update Employee">
        <EmployeeForm
          employees={employees ?? []}
          onCancelHandler={() => SetIsUpdateFormOpen(false)}
          onSubmitHandler={values => {
            mutateUpdateEmployee(values, {
              onSuccess: () => SetIsUpdateFormOpen(false),
            });
          }}
          employee={selectedEmployee}
          roles={roles ?? []}
        />
      </Popup>
    );
  };

  return (
    <>
      {CreateEmployeeFormPopup()}
      {UpdateEmployeeFormPopup()}
      <ConfirmDialog confirmDialog={deleteDialog} setConfirmDialog={setDeleteDialog} />
      <ConfirmProfileDialog confirmProfileDialog={profileDialog} setConfirmProfileDialog={setProfileDialog} />
      <Grid container margin={0.5} paddingRight={1} spacing={0.5}>
        <Grid item xs={12}>
          {AddEmployeeButton()}
        </Grid>

        <Grid item xs={12} sm={12} md={12} lg={9} xl={5.5}>
          <DataGrid
            className={classes.root}
            autoHeight
            columns={columns}
            rows={employees ?? []}
            disableSelectionOnClick={true}
          />
        </Grid>
      </Grid>
    </>
  );
};
